import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type {
  ScanInboxFile,
  ScanIntakeRoute,
  ScanIntakeRouteCreate,
  ScanIntakeStatusDto,
} from '@dcc-erp/shared';

import { api, apiJson } from '../api';

const key = (companyId: string | undefined) => ['scan-intake', companyId] as const;

export function useScanStatus(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'status'],
    queryFn: () => api<ScanIntakeStatusDto>(`/companies/${companyId}/scan-intake/status`),
    enabled: !!companyId,
  });
}

export function useScanRoutes(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'routes'],
    queryFn: () => api<ScanIntakeRoute[]>(`/companies/${companyId}/scan-intake/routes`),
    enabled: !!companyId,
  });
}

export function useCreateScanRoute(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: ScanIntakeRouteCreate) =>
      apiJson<ScanIntakeRouteCreate, ScanIntakeRoute>(
        'POST',
        `/companies/${companyId}/scan-intake/routes`,
        dto,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDeleteScanRoute(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>('DELETE', `/companies/${companyId}/scan-intake/routes/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useScanQuarantine(companyId: string | undefined) {
  return useQuery({
    queryKey: [...key(companyId), 'quarantine'],
    queryFn: () => api<ScanInboxFile[]>(`/companies/${companyId}/scan-intake/quarantine`),
    enabled: !!companyId,
  });
}

export function useReleaseScanFile(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>(
        'POST',
        `/companies/${companyId}/scan-intake/quarantine/${id}/release`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}

export function useDiscardScanFile(companyId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiJson<undefined, void>(
        'POST',
        `/companies/${companyId}/scan-intake/quarantine/${id}/discard`,
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key(companyId) }),
  });
}
